/**
 * MAHI — discover-feed prefetch cache.
 *
 * Strategy (fast path first):
 *   1. Fetch pre-built feed snapshot from CloudFront CDN
 *      (mira/feed/women.json or men.json, built nightly after the crawl).
 *      Cold CDN hit: ~30–80ms.  Warm CDN hit (cached at edge): ~5–20ms.
 *      The snapshot contains 2,000 products; we shuffle client-side and take
 *      the first 40 — giving enormous variety without a live scan.
 *
 *   2. Fall back to the live GET /inventory/dresses API if:
 *      - CDN URL is not configured (REACT_APP_IMAGE_CDN_BASE absent)
 *      - The snapshot fetch fails (network error, 404 on first deploy)
 *      - The snapshot contains fewer products than requested
 *      - The user's discovery preferences leave too little of it to swipe
 *        through (see MIN_FILTERED_SNAPSHOT below)
 *
 * Discovery preferences are per-user and the snapshot is one shared object per
 * gender, so they cannot be baked into it — they are applied here, in the
 * browser, by discoveryPrefs.js. That keeps the fast path fast for anyone with
 * broad picks. Narrow picks fall through to the API, which applies the same
 * filter over the whole catalogue and can widen into adjacent categories when
 * a pool runs thin — something a static snapshot cannot do.
 *
 * Prefetch timing: kicked off at app boot for anyone who already holds a
 * token, and immediately after signup/OTP/Google for anyone who doesn't —
 * see `warmDiscoverFeed()`. Either way the feed is in memory by the time the
 * user reaches /discover.
 *
 * Only the gender we actually need is fetched. This used to request both
 * snapshots unconditionally (~300KB) to serve one ~150KB answer; the user's
 * gender is in the cached user object on every visit after the first, and
 * `female` is the right guess before then (it is what DiscoveryFeed itself
 * defaults to, and the catalog is overwhelmingly women's).
 */
import mahi from './api';
import { filterByPrefs, prefsFromUser, prefsSignature } from './discoveryPrefs';

// CloudFront base URL injected at build time.  Falls back to empty string
// (which disables CDN fast path) so local dev and prod-without-CDN still work.
const CDN_BASE = (process.env.REACT_APP_IMAGE_CDN_BASE || '').replace(/\/$/, '');

// '<gender>|<preference signature>' -> Promise<{ items, notice }>. Keyed on
// the preferences too, so saving a new set of picks cannot be served the deck
// the old ones produced.
//
// `notice` is MAHI's own line about the deck she just built — "I'm running low
// on sarees for you, want me to mix in some lehengas?" — and it rides along
// with the items rather than being fetched separately because it is only ever
// true *of this particular deck*. Only the API path can produce one; the CDN
// snapshot has no idea what the whole catalogue holds.
const cache = {};

/**
 * What this person's feed is filtered to, from the cached user object.
 *
 * Mirrors services/mahi/feed.resolve on the backend, and must keep mirroring
 * it: this picks which pre-built CDN snapshot to fetch, and the API path
 * resolves the same question server-side, so a disagreement shows up as the
 * warm deck and the live deck being about different people.
 *
 * Returns '' for "show me everything", which is a real answer and not a
 * missing one — `fetchFromCDN` has no snapshot for it and deliberately falls
 * through to the API rather than quietly serving womenswear.
 */
export function feedGenderFor(user) {
  const picked = String(user?.shopping_for || '').trim().toLowerCase();
  if (picked === 'menswear') return 'male';
  if (picked === 'womenswear') return 'female';
  if (picked === 'everything') return '';
  return String(user?.gender || '').trim().toLowerCase() === 'male' ? 'male' : 'female';
}

/** Normalise anything callers pass into the only two genders the CDN has. */
function genderKey(gender) {
  if (gender === '') return '';          // "everything" — API only, see above
  return gender === 'male' ? 'male' : 'female';
}

// Below this many products surviving the preference filter, the snapshot is
// not worth serving and we go to the API instead.
//
// The number is a deck, not a handful. The snapshot can only *filter* — it has
// no way to widen — so a narrow pick that scrapes past this threshold gets a
// short deck of exact matches and none of MAHI's line about running low, while
// the same pick sent to the API comes back three times longer with the
// widening explained. Measured on the 2,000-product snapshot, "Jeans &
// trousers" survives at ~20 and the API answers the same pick with ~62.
//
// So the bar is the deck size the feed actually serves (40), not the point at
// which a deck becomes unusable (12, which is what this used to be — chosen
// when the snapshot held 300 products and falling through was expensive).
const MIN_FILTERED_SNAPSHOT = 40;

/** The preferences to filter with when a caller doesn't pass any.
 *
 * Read from the cached /auth/me user rather than fetched: this runs at app
 * boot, before React mounts, and a round trip here would put a network call in
 * front of the one screen the whole prefetch exists to make instant.
 */
function cachedPrefs() {
  try {
    const raw = localStorage.getItem('mahi_user_cache');
    return prefsFromUser(raw ? JSON.parse(raw) : null);
  } catch {
    return prefsFromUser(null);
  }
}

/** Shuffle an array in-place (Fisher-Yates). */
function shuffle(arr) {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

/**
 * Start downloading the first card's opening photo the moment we know what it
 * is, rather than when React gets around to mounting the card.
 *
 * That gap is not small: the snapshot resolves in tens of milliseconds, but
 * the <img> that needs it does not exist until the feed has been set into
 * state, the component tree has re-rendered and the browser has laid the card
 * out. On a mid-range phone that is several hundred milliseconds during which
 * the connection to the CDN — already open, already warm — sits idle.
 *
 * Deliberately fire-and-forget and deliberately only the first photo: the
 * point is to win the race for the one image the user is waiting on, and
 * queueing more here would recreate exactly the bandwidth contention that
 * ProductSwipeCard's windowed roll exists to avoid.
 */
function preloadHeroImage(items) {
  const src = items?.[0]?.image_urls?.[0];
  if (!src || typeof window === 'undefined') return;
  try {
    const img = new Image();
    // Same hint the real <img> carries, so the two requests coalesce into one
    // high-priority fetch rather than the preload being deprioritised and
    // effectively wasted.
    img.fetchPriority = 'high';
    img.decoding = 'async';
    img.src = src;
  } catch { /* Image() unavailable (SSR, exotic runtime) — nothing to warm */ }
}

/** Fetch from the pre-built CDN snapshot. Returns null on any failure. */
async function fetchFromCDN(gender, prefs) {
  if (!CDN_BASE) return null;
  // No snapshot covers an unfiltered feed. Falling back to women.json here
  // would silently answer "everything" with "womenswear", which is the exact
  // failure the picker exists to prevent.
  if (!gender) return null;
  const label = gender === 'male' ? 'men' : 'women';
  const url = `${CDN_BASE}/mira/feed/${label}.json`;
  try {
    const res = await fetch(url, { cache: 'default' }); // browser cache aware
    if (!res.ok) return null;
    const data = await res.json();
    const items = (data.items || []).filter((d) => d.image_urls?.length);
    if (!items.length) return null;
    // Filtered before the sub-sample, not after: taking 40 at random and then
    // dropping the ones that don't match would leave a deck whose size swung
    // wildly with the shuffle, and would starve narrow picks the snapshot
    // actually has enough of.
    const wanted = filterByPrefs(items, prefs);
    if (wanted.length < MIN_FILTERED_SNAPSHOT) return null;
    // Client-side shuffle: pick 40 from the (filtered) pool.
    // Unfiltered that is ~300C40 combinations — effectively unlimited variety.
    return shuffle(wanted).slice(0, 40);
  } catch {
    return null;
  }
}

/** Live API fallback. Applies the preference filter server-side, over the
 *  whole catalogue, and widens into adjacent categories when a pool runs
 *  thin — so no preferences are passed up: the account already carries them. */
function fetchFromAPI(gender) {
  return mahi
    .get(`/inventory/dresses?${new URLSearchParams({ limit: '40', gender })}`)
    .then(({ data }) => ({
      items: (data.items || []).filter((d) => d.image_urls?.length),
      notice: data.preferences?.message || '',
    }))
    .catch(() => ({ items: null, notice: '' }));
}

async function fetchFeed(gender, prefs) {
  const fromCDN = await fetchFromCDN(gender, prefs);
  // CDN unavailable, not configured, or filtered down past the point of being
  // a deck — fall through to the live API.
  const feed = fromCDN?.length ? { items: fromCDN, notice: '' } : await fetchFromAPI(gender);
  if (feed.items?.length) preloadHeroImage(feed.items);
  return feed;
}

/**
 * Start (or reuse) the prefetch for one gender. Safe to call repeatedly —
 * the in-flight promise is shared, so N callers cause one request.
 */
export function prefetchDiscoverFeed(gender, prefs) {
  const resolved = prefs || cachedPrefs();
  const g = genderKey(gender);
  const key = `${g}|${prefsSignature(resolved)}`;
  if (!cache[key]) {
    cache[key] = fetchFeed(g, resolved).then((feed) => {
      // A null/empty result means both the CDN snapshot and the live API
      // failed - almost always a transient hiccup, not a standing outage.
      // Caching that would mean every later visit to /discover (this
      // component remounts on every nav back to it) replays the same
      // failure forever, with no new network request to ever recover from
      // it - which is exactly what made this look like a persistent bug
      // with nothing to show for it in the backend logs. Evict it instead,
      // so the next call gets a genuine retry.
      if (!feed.items?.length) delete cache[key];
      return feed;
    });
  }
  return cache[key];
}

/**
 * The feed for a gender, starting the fetch if nothing warmed it first.
 * Resolves to `{ items, notice }`; `items` is null when both tiers failed.
 *
 * Always returns a promise rather than null-when-cold. The old contract
 * (null if no prefetch had ever fired) meant every returning user — whose
 * prefetch was never triggered, because it only ran on the signup and OAuth
 * paths — fell through to a cold serial fetch inside the feed component.
 */
export function getPrefetchedFeed(gender, prefs) {
  return prefetchDiscoverFeed(gender, prefs);
}

/**
 * A genuinely new deck, straight from the API — no snapshot, no cache.
 *
 * For the one moment the fast path is wrong: coming back to the default feed
 * after a MAHI search, having already swiped through part of it. The cached
 * promise would replay the deck they just worked through, and the CDN snapshot
 * is a shared, static object that cannot know what this person has seen. The
 * live route can — it filters out everything they have already had a verdict
 * on (see routes/mira/inventory.py::list_dresses) — and this is not first
 * paint, so it can afford the round trip the boot path cannot.
 *
 * Resolves to the same `{ items, notice }`; `items` is null on failure, which
 * callers should read as "keep what you have".
 */
export function fetchFreshFeed(gender) {
  return fetchFromAPI(genderKey(gender));
}

/**
 * Drop every cached deck. Called when someone changes their preferences —
 * without it the feed they just narrowed would keep serving the promise the
 * old picks resolved, and the change would look like it did nothing until the
 * next full page load.
 */
export function resetDiscoverFeed() {
  Object.keys(cache).forEach((key) => { delete cache[key]; });
}

/**
 * Boot-time warm-up. Called once from index.js, before React mounts.
 *
 * Only warms for someone who already holds a token. Since guest mode that is
 * almost everyone — index.html mints a session before the bundle parses — so
 * the check now mostly catches the visitor whose mint is still in flight or
 * failed outright, for whom there is no user_id to build a deck against yet.
 */
export function warmDiscoverFeed() {
  try {
    if (!localStorage.getItem('mira_token')) return;
    // The cached user is written by auth.js on every /auth/me, so from the
    // second visit onward the gender is known here without a network call.
    const raw = localStorage.getItem('mahi_user_cache');
    const cached = raw ? JSON.parse(raw) : null;
    // Someone who has not answered the picker yet has no feed to warm — the
    // answer is what decides which one, and guessing would mean fetching a
    // deck we are about to throw away.
    if (!cached?.shopping_for && !cached?.gender) return;
    prefetchDiscoverFeed(feedGenderFor(cached), prefsFromUser(cached));
  } catch { /* private mode / corrupt cache — the feed still loads on demand */ }
}
